import { Component, Input, OnInit } from "@angular/core";
import { InsideSurveyTab } from "../../../models/insideSurveyTab.interface";
import { ValidationError } from "../../../../../../shared/Models/ValidationError";
import { forkJoin, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { Building, InsideSurvey } from "src/app/shared/Models/read";
import { DeviceCheckService } from "src/app/core/survey/shared/services/device-check.service";
import { SavingStateService } from "src/app/core/survey/shared/services/saving-state.service";
import { ToastService } from "src/app/shared/toast.service";
import { SaveOutsideSurveyMainAddressDto } from "src/app/shared/Models/write/SaveOutsideSurveyMainAddressDto";
import { AddressTypeEnum } from "src/app/shared/Models/enums/AddressTypeEnum";

@Component({
  selector: "app-inside-survey-address-info",
  templateUrl: "./inside-survey-address-info.component.html",
  styleUrls: ["./inside-survey-address-info.component.scss"],
})
export class InsideSurveyAddressInfoComponent implements InsideSurveyTab {
  @Input() buildings: Building[];
  @Input() survey: InsideSurvey;
  @Input() isAuthCanWrite: boolean;

  tabId = "addressInfo";
  changed: boolean = false;

  get inPortrait() {
    return this._deviceCheckService.onPortrait();
  }

  get onTablet() {
    return this._deviceCheckService.onTablet();
  }

  get fullName() {
    return [
      this.survey?.insideEngineer?.firstName,
      this.survey?.insideEngineer?.lastName,
    ].join(" ");
  }

  constructor(
    private _deviceCheckService: DeviceCheckService,
    private _savingStateService: SavingStateService,
    private _toastService: ToastService
  ) {}

  getValidationError(checkValidity?: boolean): ValidationError {
    let valError;
    if (checkValidity)
      for (let building of this.buildings)
        if (!building.addresses.find((a) => a.isMainAddress)) {
          valError = new ValidationError(
            $localize`:@@general.warning:Warning`,
            $localize`:@@validation.survey.address.mainaddress:Building needs a main address`,
            $localize`:@@survey.inside.outside.units.title:Inside Survey, Address`
          );
        } else {
          const mainaddress = building.addresses.find((a) => a.isMainAddress);
          const allAddressesNotInUse = building.addresses.every(
            (a) => a.addressType === AddressTypeEnum.NotInUse
          );
          if (
            mainaddress.addressType == AddressTypeEnum.NotInUse &&
            !allAddressesNotInUse
          )
            valError = new ValidationError(
              $localize`:@@general.warning:Warning`,
              $localize`:@@validation.survey.address.mainaddressType:Please select a main address of a type other than NotInUse`,
              $localize`:@@survey.outside.address.title:Outside Survey, Address`
            );
        }
    return valError;
  }

  save(checkValidity?: boolean): Observable<boolean> {
    if (checkValidity) {
      const valError = this.getValidationError(checkValidity);
      if (valError) {
        this._toastService.showWarnToast(valError.title, valError.message);
        return of(false);
      }
    }
    if (!this.changed) return of(true);

    if (this.isAuthCanWrite) {
      this.changed = false;
      return forkJoin(
        this.buildings.map((building) => {
          return this._savingStateService.addInsideSurveyAddress(
            this.createDto(building)
          );
        })
      ).pipe(map((val) => val.every((v) => !!v)));
    }
  }

  updateMainAddress(buildingId: string, adressId: string) {
    const building = this.buildings.find((b) => b.id == buildingId);
    building.addresses.forEach((a) => {
      a.isMainAddress = a.id === adressId;
    });
    this.changed = true;
  }

  createDto(building: Building) {
    return new SaveOutsideSurveyMainAddressDto(
      this.survey.id,
      building.id,
      building.getMainAddress().id
    );
  }
}
